import axios from 'axios';

// Configuration de base pour axios
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token JWT à chaque requête
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('era_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('era_token');
      localStorage.removeItem('era_user');
      const isElectron = navigator.userAgent.toLowerCase().includes('electron');
      if (isElectron) {
        window.location.hash = '#/login';
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API Services
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
  logout: () => {
    localStorage.removeItem('era_token');
    localStorage.removeItem('era_user');
  },
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const portfolioService = {
  getData: () => api.get('/portfolio'),
};

export const customFieldService = {
  getProjectFields: (projectId) => api.get(`/custom-fields/project/${projectId}`),
  createField: (projectId, data) => api.post(`/custom-fields/project/${projectId}`, data),
  updateField: (fieldId, data) => api.put(`/custom-fields/${fieldId}`, data),
  deleteField: (fieldId) => api.delete(`/custom-fields/${fieldId}`),
  getTaskValues: (taskId) => api.get(`/custom-fields/task/${taskId}/values`),
  setTaskValues: (taskId, values) => api.put(`/custom-fields/task/${taskId}/values`, { values }),
};

export const projectService = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (projectData) => api.post('/projects', projectData),
  update: (id, projectData) => api.put(`/projects/${id}`, projectData),
  delete: (id) => api.delete(`/projects/${id}`),
  deletePermanently: (id) => api.delete(`/projects/${id}/hard`),
  getDashboardStats: () => api.get('/projects/dashboard-stats'),
  analyze: (id) => api.post(`/projects/${id}/analyze`, {}, { timeout: 60000 }), // Timeout augmenté à 60s pour l'IA
  getShareLink: (id) => api.get(`/projects/${id}/share-link`),
  inviteUser: (id, email, role, functionalRole) => api.post(`/projects/${id}/invite`, { email, role, functionalRole }),
  updateMember: (projectId, userId, data) => api.put(`/projects/${projectId}/members/${userId}`, data),
  getAccessRequests: (id) => api.get(`/projects/${id}/access-requests`),
  respondToRequest: (id, requestId, status) => api.post(`/projects/${id}/access-requests`, { requestId, status }),
  requestAccessByToken: (token) => api.post('/projects/join-by-token', { token }),
  getInviteInfo: (token) => api.get(`/projects/invite-info/${token}`),
  acceptInvitation: (token) => api.post('/projects/accept-invite', { token }),
  getMyInviteToken: (id) => api.get(`/projects/${id}/my-invite-token`),
  exportERAProject: (id) => api.get(`/export/era-project/${id}`, { responseType: 'blob' }),
};

export const aiService = {
  generatePlan: (data) => api.post('/ai/generate-plan', data, { timeout: 90000 }), // Timeout 90s pour génération complexe
  chat: (data) => api.post('/ai/chat', data),
  analyzeProject: (projectId) => api.get(`/ai/analyze/${projectId}`, { timeout: 60000 }),
  suggestPriorities: (projectId) => api.get(`/ai/priorities/${projectId}`),
  estimateDuration: (data) => api.post('/ai/estimate-duration', data),
  smartSchedule: (data) => api.post('/ai/smart-schedule', data, { timeout: 60000 }),
  getDailyPriorities: (data) => api.post('/ai/daily-priorities', data, { timeout: 60000 }),
  recordPriorityFeedback: (data) => api.post('/ai/priority-feedback', data),
  updatePreferences: (data) => api.post('/ai/preferences', data),
  executeCommand: (data) => api.post('/ai/command', data),
  // Fonctionnalités IA pour la messagerie
  suggestReplies: (data) => api.post('/ai/messages/suggest-replies', data),
  summarizeConversation: (messages) => api.post('/ai/messages/summarize', { messages }),
  detectUrgency: (messageContent) => api.post('/ai/messages/detect-urgency', { messageContent }),
  analyzeTone: (messageContent) => api.post('/ai/messages/analyze-tone', { messageContent }),
};

export const meetingService = {
  getAll: (projectId) => api.get(`/meetings${projectId ? '?project_id=' + projectId : ''}`),
  getById: (id) => api.get(`/meetings/${id}`),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  delete: (id) => api.delete(`/meetings/${id}`),
};


export const calendarService = {
  getEvents: (types) => api.get('/calendar', { params: types && types.length < 3 ? { types: types.join(',') } : {} }),
};

export const templateService = {
  getAll: () => api.get('/templates'),
  getById: (id) => api.get(`/templates/${id}`),
  createProject: (templateId, data) => api.post(`/templates/${templateId}/use`, data),
};

export const taskService = {
  getByProject: (projectId, filters) => {
    const params = new URLSearchParams(filters).toString();
    return api.get(`/tasks/projects/${projectId}${params ? '?' + params : ''}`);
  },
  getById: (taskId) => api.get(`/tasks/${taskId}`),
  create: (taskData) => api.post('/tasks', taskData),
  update: (id, taskData) => api.put(`/tasks/${id}`, taskData),
  delete: (id) => api.delete(`/tasks/${id}`),
  updateStatus: (id, status) => api.patch(`/tasks/${id}/status`, { status }),
  addTag: (taskId, tagId) => api.post(`/tasks/${taskId}/tags/${tagId}`),
  removeTag: (taskId, tagId) => api.delete(`/tasks/${taskId}/tags/${tagId}`),
  addDependency: (taskId, data) => api.post(`/tasks/${taskId}/dependencies`, data),
  removeDependency: (taskId, dependencyId) => api.delete(`/tasks/${taskId}/dependencies/${dependencyId}`),
  getProjectPhases: (projectId) => api.get(`/tasks/projects/${projectId}/phases`),
  updateOrder: (tasks) => api.put('/tasks/order', { tasks }),
  createSubtask: (taskId, data) => api.post(`/tasks/${taskId}/subtasks`, data),
};

export const messageService = {
  getByTask: (taskId) => api.get(`/messages/task/${taskId}`),
  createTaskMessage: (taskId, data) => api.post(`/messages/task/${taskId}`, data),
  getByProject: (projectId) => api.get(`/messages/project/${projectId}`),
  createProjectMessage: (projectId, data) => api.post(`/messages/project/${projectId}`, data),
  togglePin: (messageId) => api.put(`/messages/${messageId}/pin`),
  addReaction: (messageId, emoji) => api.post(`/messages/${messageId}/reactions`, { emoji }),
  removeReaction: (messageId, emoji) => api.delete(`/messages/${messageId}/reactions`, { data: { emoji } }),
  markAsRead: (messageId) => api.put(`/messages/${messageId}/read`),
  // Mentions
  getMyMentions: (projectId) => api.get(`/messages/mentions/me${projectId ? '?projectId=' + projectId : ''}`),
  getMentionSuggestions: (projectId, query) => api.get(`/messages/mentions/suggestions/${projectId}${query ? '?query=' + query : ''}`),
  // Threads
  getThreadReplies: (messageId) => api.get(`/messages/${messageId}/replies`),
  replyToMessage: (messageId, data) => api.post(`/messages/${messageId}/reply`, data),
  // Recherche et filtres
  search: (projectId, filters) => api.get(`/messages/search/${projectId}`, { params: filters }),
  // Archivage
  toggleArchive: (messageId) => api.put(`/messages/${messageId}/archive`),
  // Priorité
  updatePriority: (messageId, priority) => api.put(`/messages/${messageId}/priority`, { priority }),
  // Tags
  createTag: (projectId, data) => api.post('/messages/tags', { project_id: projectId, ...data }),
  getProjectTags: (projectId) => api.get(`/messages/tags/project/${projectId}`),
  addTag: (messageId, tagId) => api.post(`/messages/${messageId}/tags`, { tag_id: tagId }),
  removeTag: (messageId, tagId) => api.delete(`/messages/${messageId}/tags/${tagId}`),
};

export const channelService = {
  create: (data) => api.post('/channels', data),
  getByProject: (projectId) => api.get(`/channels/project/${projectId}`),
  getById: (channelId) => api.get(`/channels/${channelId}`),
  update: (channelId, data) => api.put(`/channels/${channelId}`, data),
  delete: (channelId) => api.delete(`/channels/${channelId}`),
  addMember: (channelId, userId, role) => api.post(`/channels/${channelId}/members`, { user_id: userId, role }),
  removeMember: (channelId, userId) => api.delete(`/channels/${channelId}/members/${userId}`),
  updateNotifications: (channelId, enabled) => api.put(`/channels/${channelId}/notifications`, { notification_enabled: enabled }),
};

export const notificationService = {
  getUnread: () => api.get('/notifications/unread'),
  getAll: () => api.get('/notifications/all'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  getEmailPreferences: () => api.get('/notifications/email-preferences'),
  updateEmailPreferences: (prefs) => api.put('/notifications/email-preferences', prefs),
};

export const attachmentService = {
  upload: (formData) => api.post('/attachments/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getByRelated: (type, id) => api.get(`/attachments/${type}/${id}`),
  delete: (id) => api.delete(`/attachments/${id}`),
  getDownloadUrl: (id) => `${api.defaults.baseURL}/attachments/download/${id}`
};

export const importService = {
  importERAProject: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/era-project', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importTasks: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    return api.post('/import/tasks', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importProjects: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/projects', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importProjectWithTasks: (data) => api.post('/import/project-with-tasks', data),
  getTemplate: (type) => api.get(`/import/template/${type}`),
};

export const paymentService = {
  initiate: (data) => api.post('/payments/initiate', data),
  checkStatus: (reference) => api.get(`/payments/status/${reference}`),
  getHistory: () => api.get('/payments/history'),
};

export const exportService = {
  exportTasks: (projectId) => {
    return api.get(`/export/tasks/${projectId}`, {
      responseType: 'blob'
    });
  },
  exportProjects: () => {
    return api.get('/export/projects', {
      responseType: 'blob'
    });
  },
  exportProjectComplete: (projectId) => {
    return api.get(`/export/project-complete/${projectId}`, {
      responseType: 'blob'
    });
  },
  exportUserData: () => {
    return api.get('/export/user-data', {
      responseType: 'blob'
    });
  },
  exportProjectPDF: (projectId) => {
    return api.get(`/export/pdf/${projectId}`, {
      responseType: 'blob'
    });
  },
  exportERAProject: (projectId) => {
    return api.get(`/export/era-project/${projectId}`, {
      responseType: 'blob'
    });
  },
  // Fonction utilitaire pour télécharger un fichier blob
  downloadBlob: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

export default api;

export const milestoneService = {
  getByProject: (projectId) => api.get(`/milestones/project/${projectId}`),
  create: (projectId, data) => api.post(`/milestones/project/${projectId}`, data),
  update: (id, data) => api.put(`/milestones/${id}`, data),
  delete: (id) => api.delete(`/milestones/${id}`),
};

export const ticketService = {
  submit: (data) => api.post('/tickets', data),
  getMyTickets: () => api.get('/tickets/my'),
  getById: (id) => api.get(`/tickets/${id}`),
};

export const reportService = {
  // Admin/owner only
  generate: (projectId, body = {}) => api.post(`/reports/generate/${projectId}`, body, { timeout: 120000 }),
  save: (projectId, content) => api.post(`/reports/save/${projectId}`, content),
  exportPDF: (projectId, content) => api.post(`/reports/export-pdf/${projectId}`, content, { responseType: 'blob', timeout: 60000 }),
  exportWord: (projectId, content) => api.post(`/reports/export-word/${projectId}`, content, { responseType: 'blob', timeout: 60000 }),
  deleteReport: (reportId) => api.delete(`/reports/${reportId}`),
  // All members
  listReports: (projectId) => api.get(`/reports/project/${projectId}`),
  getContent: (reportId) => api.get(`/reports/${reportId}/content`),
  downloadPDF: (reportId) => api.get(`/reports/${reportId}/download-pdf`, { responseType: 'blob', timeout: 60000 }),
  downloadWord: (reportId) => api.get(`/reports/${reportId}/download-word`, { responseType: 'blob', timeout: 60000 }),
};

export const adminService = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (id, newPassword) => api.post(`/admin/users/${id}/reset-password`, { newPassword }),
  verifyUserEmail: (id) => api.post(`/admin/users/${id}/verify`),
  resendVerification: (id) => api.post(`/admin/users/${id}/resend-verification`),
  getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
  getSubscriptionStats: () => api.get('/admin/subscriptions/stats'),
  createSubscription: (data) => api.post('/admin/subscriptions', data),
  updateSubscription: (id, data) => api.put(`/admin/subscriptions/${id}`, data),
  getTickets: (params) => api.get('/admin/tickets', { params }),
  getTicketById: (id) => api.get(`/admin/tickets/${id}`),
  updateTicket: (id, data) => api.put(`/admin/tickets/${id}`, data),
  getActivityLog: (params) => api.get('/admin/activity', { params }),
  // Email marketing
  sendEmail: (data) => api.post('/admin/emails/send', data),
  getEmailHistory: (params) => api.get('/admin/emails/history', { params }),
  searchEmailUsers: (q) => api.get('/admin/emails/users-search', { params: { q } }),
};

export const sprintService = {
  getByProject: (projectId) => api.get(`/sprints/project/${projectId}`),
  create: (projectId, data) => api.post(`/sprints/project/${projectId}`, data),
  update: (id, data) => api.put(`/sprints/${id}`, data),
  delete: (id) => api.delete(`/sprints/${id}`),
  start: (id, data) => api.post(`/sprints/${id}/start`, data || {}),
  complete: (id, moveIncomplete) => api.post(`/sprints/${id}/complete`, { moveIncomplete }),
  getBurndown: (id) => api.get(`/sprints/${id}/burndown`),
  moveTask: (taskId, data) => api.patch(`/sprints/tasks/${taskId}/move`, data),
};

export const timeTrackingService = {
  startTimer:      (taskId)    => api.post('/time-tracking/timer/start', { task_id: taskId }),
  stopTimer:       (taskId)    => api.post('/time-tracking/timer/stop',  { task_id: taskId }),
  getActiveTimer:  (projectId) => api.get(`/time-tracking/timer/active/${projectId}`),
  createEntry:     (data)      => api.post('/time-tracking/entries', data),
  deleteEntry:     (id)        => api.delete(`/time-tracking/entries/${id}`),
  getReport:       (projectId) => api.get(`/time-tracking/report/${projectId}`),
  exportTimesheet: (projectId) => api.get(`/time-tracking/export/${projectId}`, { responseType: 'blob' }),
};

export const automationService = {
  getByProject: (projectId)  => api.get(`/automations/project/${projectId}`),
  create:       (data)       => api.post('/automations', data),
  update:       (id, data)   => api.put(`/automations/${id}`, data),
  delete:       (id)         => api.delete(`/automations/${id}`),
  getLogs:      (projectId)  => api.get(`/automations/logs/${projectId}`),
  overdueCheck: (projectId)  => api.post(`/automations/overdue-check/${projectId}`),
};

export const workloadService = {
  getWorkload:       (projectId)                  => api.get(`/workload/project/${projectId}`),
  updateCapacities:  (projectId, capacities)      => api.put(`/workload/capacity/${projectId}`, { capacities }),
  rebalance:         (projectId)                  => api.post(`/workload/rebalance/${projectId}`),
  applyRebalance:    (projectId, actions)         => api.post(`/workload/apply/${projectId}`, { actions }),
};

export const budgetService = {
  getBudget:      (projectId)        => api.get(`/budget/project/${projectId}`),
  updateSettings: (projectId, data)  => api.put(`/budget/project/${projectId}`, data),
  updateRates:    (projectId, rates) => api.put(`/budget/rates/${projectId}`, { rates }),
  createExpense:  (projectId, data)  => api.post(`/budget/expenses/${projectId}`, data),
  updateExpense:  (id, data)         => api.put(`/budget/expenses/${id}`, data),
  deleteExpense:  (id)               => api.delete(`/budget/expenses/${id}`),
};

export const apiTokenService = {
  list:   ()         => api.get('/auth/tokens'),
  create: (data)     => api.post('/auth/tokens', data),
  revoke: (id)       => api.delete(`/auth/tokens/${id}`),
};

export const webhookService = {
  listEvents: ()          => api.get('/webhooks/events'),
  list:       ()          => api.get('/webhooks'),
  create:     (data)      => api.post('/webhooks', data),
  update:     (id, data)  => api.put(`/webhooks/${id}`, data),
  remove:     (id)        => api.delete(`/webhooks/${id}`),
  test:       (id)        => api.post(`/webhooks/${id}/test`),
  deliveries: (id)        => api.get(`/webhooks/${id}/deliveries`),
};
